import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Urls } from '@/apiConnection/urls';
import { LoadingComponent } from '@/components/loading/loading.component';
import { Utils } from '@/utils/utils';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { DatePipe } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SAUModalService } from '@some-angular-utils/modal';
import { SAUTableModule } from '@some-angular-utils/table';
import { IntegrationFormComponent } from '../integration-form/integration-form.component';

type SyncScope = 'catalog' | 'stock' | 'import' | 'customers';
type ReportTab = 'matched' | 'only_in_erp' | 'only_in_shop';
type OrdersTab = 'imported' | 'pending' | 'ignored';

const ORDER_STATUSES: Record<string, string> = {
  pending: 'Pendiente de pago',
  processing: 'Procesando',
  'on-hold': 'En espera',
  completed: 'Completado',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
  failed: 'Fallido',
  trash: 'En la papelera',
  'checkout-draft': 'Borrador',
};

/**
 * One WooCommerce shop: status, catalog sync actions, unmatched-products report,
 * manual order import and the integration's log. Every action only queues a job on
 * the backend (202), so results show up after "Actualizar", not in the response.
 */
@Component({
  imports: [
    RouterModule,
    FormsModule,
    DatePipe,
    SAUTableModule,
    LoadingComponent,
    CentToEurDisplayDirective,
  ],
  templateUrl: './integration-detail.page.html',
  styleUrl: './integration-detail.page.scss',
  encapsulation: ViewEncapsulation.None
})
export class IntegrationDetailPage {

  @ViewChild('sauTable')
  sauTable: { getItems: () => void; refresh: () => void; } | undefined;

  private apiService = inject(ApiService);
  private authService = inject(AuthenticationService);
  private sauModalService = inject(SAUModalService);
  private route = inject(ActivatedRoute);

  public id = Number(this.route.snapshot.paramMap.get('id'));
  public baseUrl = `${Urls.urls.integrations}/${this.id}`;
  public logsUrl = `${this.baseUrl}/logs`;

  public loading = signal(true);
  public busy = signal<string | null>(null);
  public integration = signal<any>(null);
  public report = signal<any>(null);
  public reportTab = signal<ReportTab>('only_in_erp');
  public ordersReport = signal<any>(null);
  public ordersReportError = signal<string | null>(null);
  public ordersTab = signal<OrdersTab>('imported');
  public customersReport = signal<any>(null);
  public customersReportError = signal<string | null>(null);
  public customersTab = signal<ReportTab>('matched');

  public importSince = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  public today = new Date().toISOString().slice(0, 10);

  public canUpdate = this.authService.hasPanel('integrations', 'can_update');

  public logHeaders = [
    { name: 'FECHA', key: 'created_at', type: 'dateTime' },
    { name: 'SENTIDO', key: 'direction' },
    { name: 'ENTIDAD', key: 'entity' },
    { name: 'ID TIENDA', key: 'external_id' },
    { name: 'ESTADO', key: 'status' },
    { name: 'MENSAJE', key: 'message' },
  ];

  public logFilterConfig: any = {
    orderParamName: 'sort',
    orderByFields: [
      { field: 'created_at', label: 'Fecha', defaultValue: false },
    ],
    order: ['status', 'entity', 'direction', 'dateRange'],
    mobile: ['status', 'dateRange'],
    form: {
      status: {
        name: 'Estado',
        key: 'status',
        type: 'selectSimple',
        dropdowns: [
          { id: 'ok', name: 'Correcto' },
          { id: 'error', name: 'Error' },
          { id: 'skipped', name: 'Omitido' },
        ],
      },
      entity: {
        name: 'Entidad',
        key: 'entity',
        type: 'selectSimple',
        dropdowns: [
          { id: 'product', name: 'Producto' },
          { id: 'category', name: 'Categoría' },
          { id: 'order', name: 'Pedido' },
          { id: 'client', name: 'Cliente' },
        ],
      },
      direction: {
        name: 'Sentido',
        key: 'direction',
        type: 'selectSimple',
        dropdowns: [
          { id: 'out', name: 'ERP → tienda' },
          { id: 'in', name: 'Tienda → ERP' },
        ],
      },
      dateRange: {
        name: 'Periodo',
        key: 'date_from',
        keyTo: 'date_to',
        type: 'dateRange',
        defaultValue: []
      },
    }
  };

  ngOnInit(): void {
    this.refresh();
  }

  get webhookCount(): number {
    return Object.keys(this.integration()?.webhooks ?? {}).length;
  }

  get linked(): any {
    return this.integration()?.linked ?? {};
  }

  refresh() {
    this.getIntegration();
    this.getReport();
    this.getOrdersReport();
    this.getCustomersReport();
    this.sauTable?.getItems();
  }

  getIntegration() {
    this.apiService.getById(Urls.urls.integrations, this.id).subscribe({
      next: (data: any) => {
        this.integration.set(data);
        this.loading.set(false);
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al obtener la integración');
        this.loading.set(false);
      },
    });
  }

  getReport() {
    this.apiService.getPetition(`${this.baseUrl}/unmatched`).subscribe({
      next: (data: any) => this.report.set(data),
      // 404 = the catalog hasn't been analyzed yet.
      error: () => this.report.set(null),
    });
  }

  getOrdersReport() {
    if (!this.importSince) {
      return;
    }
    this.apiService.getPetition(`${this.baseUrl}/orders-report`, `?since=${this.importSince}`).subscribe({
      next: (data: any) => {
        this.ordersReport.set(data);
        this.ordersReportError.set(null);
      },
      error: (error: any) => {
        this.ordersReport.set(null);
        this.ordersReportError.set(Utils.extractErrorMessage(error) || 'No se ha podido consultar la tienda');
      },
    });
  }

  getCustomersReport() {
    this.apiService.getPetition(`${this.baseUrl}/customers-report`).subscribe({
      next: (data: any) => {
        this.customersReport.set(data);
        this.customersReportError.set(null);
      },
      error: (error: any) => {
        this.customersReport.set(null);
        this.customersReportError.set(Utils.extractErrorMessage(error) || 'No se ha podido consultar la tienda');
      },
    });
  }

  orderStatusLabel(status: string): string {
    return ORDER_STATUSES[status] ?? status;
  }

  openEditForm() {
    const modalRef = this.sauModalService.open(IntegrationFormComponent, {});
    modalRef.componentInstance.id = this.id;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getIntegration();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  testConnection() {
    this.runAction('test', this.apiService.post(`${this.baseUrl}/test`, {}), () => this.getIntegration());
  }

  sync(scope: SyncScope) {
    const run = () => this.runAction(scope, this.apiService.post(`${this.baseUrl}/sync`, { scope }));

    if (scope === 'import') {
      Utils.notification.confirm(
        'Se crearán en el ERP los productos que solo están en la tienda, usando la primera unidad de medida de la empresa. Su precio en la tienda pasa a ser el precio de compra, y el precio de ticket y las tarifas se calculan a partir de él. La tienda recibirá el precio calculado la próxima vez que cambie ese producto en el ERP. Los productos borrados con el mismo SKU se restaurarán y volverán a publicarse en la tienda. Los productos que ya existen en el ERP no se modifican.',
        run,
        () => { },
        { labels: { confirm: 'Importar productos', confirmOk: 'Importar', confirmCancel: 'Cancelar' } }
      );
      return;
    }

    if (scope === 'customers') {
      Utils.notification.confirm(
        'Los clientes de la tienda se enlazarán con el cliente del ERP que tenga su mismo email, o se crearán si no existe ninguno. Los clientes del ERP con email que aún no tengan cuenta se crearán en la tienda. Los pedidos ya importados siguen con el cliente que tenían.',
        run,
        () => { },
        { labels: { confirm: 'Sincronizar clientes', confirmOk: 'Sincronizar', confirmCancel: 'Cancelar' } }
      );
      return;
    }

    if (scope !== 'catalog') {
      run();
      return;
    }

    Utils.notification.confirm(
      'Se enlazarán los productos por SKU (referencia) y se subirán a la tienda categorías, precios y stock de todo el catálogo. Los productos que solo existen en la tienda no se tocan.',
      run,
      () => { },
      { labels: { confirm: 'Sincronizar catálogo', confirmOk: 'Sincronizar', confirmCancel: 'Cancelar' } }
    );
  }

  // Only the product analysis is a backend job; the other two reports are asked live.
  analyzeAll() {
    this.runAction('analyze', this.apiService.post(`${this.baseUrl}/sync`, { scope: 'analyze' }), () => {
      this.getOrdersReport();
      this.getCustomersReport();
    });
  }

  syncAll() {
    if (!this.importSince) {
      Utils.notification.alert('Indica desde qué fecha traer las ventas');
      return;
    }
    Utils.notification.confirm(
      `Se sincronizarán los clientes, se subirán a la tienda categorías, precios y stock de todos los productos, y se traerán las ventas modificadas desde el ${this.importSince}.`,
      () => this.runAction('all', this.apiService.post(`${this.baseUrl}/sync`, { scope: 'all', since: this.importSince })),
      () => { },
      { labels: { confirm: 'Sincronizar todo', confirmOk: 'Sincronizar', confirmCancel: 'Cancelar' } }
    );
  }

  importOrders() {
    if (!this.importSince) {
      Utils.notification.alert('Indica desde qué fecha importar');
      return;
    }
    this.runAction('import', this.apiService.post(`${this.baseUrl}/import-orders`, { since: this.importSince }));
  }

  private runAction(key: string, request$: any, onSuccess?: () => void) {
    this.busy.set(key);
    request$.subscribe({
      next: (response: any) => {
        Utils.notification.success(response?.message || 'Hecho');
        onSuccess?.();
        this.busy.set(null);
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error, 'store_url') || 'No se ha podido completar la acción');
        this.busy.set(null);
      },
    });
  }

}
